import React, { useMemo, useState } from 'react';
import Icon from '../../components/AppIcon';
import {
  getMilestoneIcon,
  getMilestoneStatusLabel,
  partitionMilestonesByOrder,
  resolveMilestoneModalType,
} from './milestoneModalConfig';

function formatPhaseStartDate(phase) {
  const raw = phase?.startDate || phase?.start_date;
  if (!raw) return null;
  try {
    return new Date(raw).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return null;
  }
}

function MilestoneButton({ item, phaseInProgress, onPress }) {
  const { milestone, state } = item;
  const icon = getMilestoneIcon(milestone.name);
  const statusLabel = getMilestoneStatusLabel(
    milestone,
    phaseInProgress ? 'in_progress' : undefined,
    state
  );
  const isDone = state === 'done';
  const isActive = state === 'active' && phaseInProgress;
  const isLocked = state === 'locked';

  const base =
    'w-full min-h-[52px] flex items-center gap-3 px-4 rounded-xl border text-left transition-colors cursor-pointer';
  let className = `${base} border-border bg-card hover:bg-muted/40`;
  if (isDone) {
    className = `${base} border-success/30 bg-success/10 hover:bg-success/15`;
  } else if (isActive) {
    className = `${base} border-primary ring-2 ring-primary/30 bg-primary/10 hover:bg-primary/15 font-semibold`;
  } else if (isLocked) {
    className = `${base} border-border/60 bg-muted/20 opacity-50 cursor-not-allowed`;
  }

  return (
    <button
      type="button"
      disabled={isLocked}
      onClick={() => onPress(milestone, state)}
      className={className}
      aria-disabled={isLocked}
    >
      <Icon
        name={isDone ? 'CheckCircle2' : icon}
        size={22}
        className={isDone ? 'text-success shrink-0' : isActive ? 'text-primary shrink-0' : 'text-muted-foreground shrink-0'}
      />
      <span className="flex-1 min-w-0">
        <span className={`block text-sm truncate ${isActive ? 'text-foreground' : 'text-foreground'}`}>
          {milestone.name}
        </span>
        <span
          className={`block text-[11px] mt-0.5 truncate ${
            isDone ? 'text-success' : isActive ? 'text-primary' : 'text-muted-foreground'
          }`}
        >
          {statusLabel}
        </span>
      </span>
      {isActive ? <Icon name="ChevronRight" size={20} className="text-primary shrink-0" /> : null}
    </button>
  );
}

/**
 * Écran poste opérateur : titre phase + boutons objectifs XL.
 */
export default function WorkshopPhaseStation({
  displayPhase,
  milestones,
  templateActionsByTemplateId = {},
  phaseAlignedWithPost = true,
  operatorRoleLabel,
  expandedWorkshopView = false,
  onMilestonePress,
}) {
  const [completedOpen, setCompletedOpen] = useState(false);

  const partitioned = useMemo(
    () => partitionMilestonesByOrder(milestones),
    [milestones]
  );

  const activeItems = partitioned.filter((p) => p.state !== 'done');
  const doneItems = partitioned.filter((p) => p.state === 'done');
  const nextActive = activeItems.find((p) => p.state === 'active');
  const otherActive = activeItems.filter((p) => p !== nextActive);
  const phaseInProgress = displayPhase?.status === 'in_progress';

  const startLabel = formatPhaseStartDate(displayPhase);

  if (!displayPhase) {
    return <p className="text-sm text-muted-foreground">Aucune phase à afficher pour ce lot.</p>;
  }

  return (
    <section className="rounded-xl border-2 border-primary/20 bg-primary/5 dark:bg-primary/10 p-4 space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-primary">
          {expandedWorkshopView ? 'Prochaine action' : 'Votre étape'}
        </p>
        {operatorRoleLabel ? (
          <span className="inline-flex w-fit items-center rounded-full bg-primary/15 text-primary text-[11px] font-semibold px-2.5 py-1 ring-1 ring-primary/25">
            {operatorRoleLabel}
          </span>
        ) : null}
      </div>

      {!phaseAlignedWithPost ? (
        <div className="rounded-lg border border-amber-500/35 bg-amber-500/[0.12] px-3 py-2 text-xs text-amber-950 dark:text-amber-100 leading-snug">
          Aucune phase ne correspond aux mots-clés attendus pour votre poste. Affichage de la phase active du lot.
        </div>
      ) : null}

      <div className="space-y-1">
        <h2 className="text-xl font-bold text-foreground leading-tight">{displayPhase.name}</h2>
        {displayPhase.description ? (
          <p className="text-sm text-muted-foreground leading-snug">{displayPhase.description}</p>
        ) : null}
        {startLabel ? (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-2">
            <Icon name="Calendar" size={14} />
            Début : {startLabel}
          </p>
        ) : displayPhase.status === 'in_progress' ? (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-2">
            <Icon name="Calendar" size={14} />
            Date de début — en cours
          </p>
        ) : null}
      </div>

      {displayPhase.status !== 'in_progress' ? (
        <p className="text-xs text-muted-foreground rounded-lg bg-muted/40 px-3 py-2">
          {displayPhase.status === 'pending'
            ? 'Cette étape n’est pas encore ouverte — phase en attente ou précédente non terminée.'
            : 'Phase en lecture seule (pas « en cours »).'}
        </p>
      ) : null}

      {milestones.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aucun objectif pour cette phase.</p>
      ) : (
        <div className="space-y-4">
          {nextActive ? (
            <div className="space-y-2">
              <p className="text-[11px] font-bold uppercase tracking-wider text-primary">Prochaine action</p>
              <MilestoneButton
                item={nextActive}
                phaseInProgress={phaseInProgress}
                onPress={onMilestonePress}
              />
            </div>
          ) : doneItems.length > 0 ? (
            <p className="text-sm text-success font-medium">Tous les objectifs de cette phase sont validés.</p>
          ) : null}

          {otherActive.length > 0 ? (
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Autres étapes
              </p>
              <div className="space-y-2">
                {otherActive.map((item) => (
                  <MilestoneButton
                    key={item.milestone.id}
                    item={item}
                    phaseInProgress={phaseInProgress}
                    onPress={onMilestonePress}
                  />
                ))}
              </div>
            </div>
          ) : null}

          {doneItems.length > 0 ? (
            <div className="pt-1 border-t border-primary/10">
              <button
                type="button"
                onClick={() => setCompletedOpen((o) => !o)}
                className="w-full flex items-center justify-between min-h-[44px] px-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-background/60 cursor-pointer"
              >
                <span>Terminé ({doneItems.length})</span>
                <Icon name={completedOpen ? 'ChevronUp' : 'ChevronDown'} size={18} />
              </button>
              {completedOpen ? (
                <div className="space-y-2 mt-2 opacity-90">
                  {doneItems.map((item) => (
                    <MilestoneButton
                      key={item.milestone.id}
                      item={item}
                      phaseInProgress={phaseInProgress}
                      onPress={onMilestonePress}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}

export { resolveMilestoneModalType };
